import CloseIcon from "@mui/icons-material/Close";
import {
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import DataGridComponent from "../../../../components/DataGridComponent";
import Loader from "../../../../components/Loader";
import {
  fetchQueueProcessBatch,
  retryMappingBatch,
  retryOutboxBatch,
  retryOutboxMessage,
} from "../../../../thunks/workflow";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../slice/snackbarSlice";
import { NOTIFICATION_MESSAGE, BATCH_DETAILS_LIMIT } from "../../../../utils/constant";
import PaginationComponent from "../../../../components/PaginationComponent";

const COLUMNS_QUEUE = [
  { field: "messageId", headerName: "Message ID", width: 380 },
  { field: "status", headerName: "Status", width: 150 },
  {
    field: "createdAt",
    headerName: "Created At",
    width: 200,
    renderCell: (params) => (
      <Typography>
        {moment(params.row.createdAt).isValid()
          ? moment(params.row.createdAt).format("DD/MM/YYYY HH:mm A")
          : "--"}
      </Typography>
    ),
  },
  {
    field: "processedAt",
    headerName: "Processed At",
    width: 200,
    renderCell: (params) => (
      <Typography>
        {moment(params.row.processedAt).isValid()
          ? moment(params.row.processedAt).format("DD/MM/YYYY HH:mm A")
          : "--"}
      </Typography>
    ),
  },
];

export const BatchDetailsDialog = ({
  open,
  onClose,
  isLoading,
  batch,
  fetchOutBoxDetails,
  batchId,
}) => {
  const dispatch = useDispatch();

  const COLUMNS = [
    { field: "azureMessageId", headerName: "Message ID", width: 360 },
    // {
    //   field: "instructionIds",
    //   headerName: "Instruction ID",
    //   width: 300,
    //   renderCell: (params) => params.row.payload.data.instructionIds.join(","),
    // },
    { field: "status", headerName: "Status", width: 150 },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 200,
      renderCell: (params) => (
        <Typography>
          {moment(params.row.createdAt).isValid()
            ? moment(params.row.createdAt).format("DD/MM/YYYY HH:mm A")
            : "--"}
        </Typography>
      ),
    },
    {
      field: "sentAt",
      headerName: "Processed At",
      width: 200,
      renderCell: (params) => (
        <Typography>
          {moment(params.row.sentAt).isValid()
            ? moment(params.row.sentAt).format("DD/MM/YYYY HH:mm A")
            : "--"}
        </Typography>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      renderCell: (params) =>
        params?.row?.status !== "SENT" && (
          <Button
            onClick={() => handleRetryOutboxMessage(params.row.id)}
            className="save_button"
          >
            Retry
          </Button>
        ),
    },
  ];
  const [tab, setTab] = useState(0);
  const [queueData, setQueueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [processingPage, setProcessingPage] = useState(1);

  // Close handler to reset tab
  const handleClose = () => {
    setTab(0);
    setOutgoingPage(1);
    setProcessingPage(1);
    onClose();
  };

  const handleChangeTabValue = (e, newValue) => {
    setTab(newValue);
    console.log("value", newValue);
    // Tab 1 → Queue Messages (fetch inbox/queue)
    if (newValue === 1) {
      setProcessingPage(1);
      fetchQueueDetails(batchId || batch?.batchId, 1, BATCH_DETAILS_LIMIT);
    } else {
      setOutgoingPage(1);
      fetchOutBoxDetails(batchId || batch?.batchId, 1, BATCH_DETAILS_LIMIT);
    }
  };

  // Retry Outbox batch
  const handleRetryOutboxBatch = async (id) => {
    try {
      setLoading(true);
      let res = await dispatch(retryOutboxBatch(id)).unwrap();
      if (res) {
        fetchOutBoxDetails(id, outgoingPage, batch?.limit || BATCH_DETAILS_LIMIT);
        dispatch(
          showSnackbar({
            show: true,
            message: NOTIFICATION_MESSAGE.OUTBOX_BATCH_RETRY_MSG,
            severity: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };
  // Retry outbox Message
  const handleRetryOutboxMessage = async (id) => {
    try {
      setLoading(true);
      let res = await dispatch(retryOutboxMessage(id)).unwrap();
      if (res) {
        dispatch(
          showSnackbar({
            show: true,
            message: NOTIFICATION_MESSAGE.OUTBOX_RETRY_MSG,
            severity: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueDetails = async (id, page = 1, limit = BATCH_DETAILS_LIMIT) => {
    try {
      setLoading(true);
      const res = await dispatch(
        fetchQueueProcessBatch({ batchId: id, page, limit })
      ).unwrap();
      setQueueData(res || null);
      dispatch(
        showSnackbar({
          show: true,
          message: NOTIFICATION_MESSAGE.QUEUE_FETCH_MSG,
          severity: "success",
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${error?.message}`,
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOutgoingPage = (event, newPage) => {
    setOutgoingPage(newPage);
    fetchOutBoxDetails(batchId || batch?.batchId, newPage, batch?.limit || BATCH_DETAILS_LIMIT);
  };

  const handleChangeProcessingPage = (event, newPage) => {
    setProcessingPage(newPage);
    fetchQueueDetails(batchId || batch?.batchId, newPage, queueData?.limit || BATCH_DETAILS_LIMIT);
  };

  const buttonFlag = batch?.items?.every((item) => item.status == "SENT");

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      {(isLoading || loading) && <Loader />}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle>
        <Typography className="serif_display_regular_26 green">
          Batch Details
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ marginRight: "5px" }}>
        <Grid xs={12}>
          <Tabs
            fullWidth
            value={tab}
            onChange={handleChangeTabValue}
            aria-label="Notes"
            sx={{
              "& .MuiTabs-indicator": { backgroundColor: "#00CE3F" },
              "& .MuiTab-root.Mui-selected": {
                color: "#00CE3F",
                fontWeight: 600,
              },
              "& .MuiTab-root": { color: "#3D4740" },
            }}
          >
            <Tab label="Outgoing Queue" />
            <Tab label="Processing Queue" />
          </Tabs>
        </Grid>

        {/* -------- Batch Info -------- */}
        <Grid container spacing={2} mb={2} mt={1}>
          <Grid item xs={6}>
            <Typography>
              <b>Batch ID:</b>{" "}
              {batch?.batchId || queueData?.batchId || batchId || "--"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>
              <b>Status:</b> {batch?.status || queueData?.status || "--"}
            </Typography>
          </Grid>
        </Grid>

        {/* -------- Outbox Messages -------- */}
        {tab === 0 && (
          <Paper sx={{ width: "100%", mt: 1 }}>
            <Grid display={"flex"} alignItems={"center"}>
              <Typography
                sx={{ padding: "10px 14px", fontWeight: 600, fontSize: 16 }}
              >
                Outbox Logs
              </Typography>
              <Button
                className="save_button"
                onClick={() =>
                  handleRetryOutboxBatch(batch?.batchId || batchId)
                }
                disabled={buttonFlag}
              >
                Process all
              </Button>
            </Grid>
            <div style={{ height: 350, width: "100%", padding: "0 12px 12px" }}>
              <DataGridComponent
                rows={batch?.items || []}
                columns={COLUMNS}
                onRowClick={() => {}}
              />
            </div>
            <div style={{ padding: "0 12px 12px" }}>
              <PaginationComponent
                totalCount={batch?.totalCount || 0}
                limit={batch?.limit || BATCH_DETAILS_LIMIT}
                page={outgoingPage}
                onChange={handleChangeOutgoingPage}
              />
            </div>
          </Paper>
        )}

        {/* -------- Queue Messages -------- */}
        {tab === 1 && (
          <Paper sx={{ width: "100%", mt: 1 }}>
            <Typography
              sx={{ padding: "10px 14px", fontWeight: 600, fontSize: 16 }}
            >
              Queue Progress
            </Typography>

            <div style={{ height: 350, width: "100%", padding: "0 12px 12px" }}>
              <DataGridComponent
                rows={queueData?.QueueMessage || []}
                columns={COLUMNS_QUEUE}
                onRowClick={() => {}}
              />
            </div>
            <div style={{ padding: "0 12px 12px" }}>
              <PaginationComponent
                totalCount={
                  queueData?.totalCount ||
                  queueData?.pagination?.totalCount ||
                  queueData?.QueueMessage?.length ||
                  0
                }
                limit={queueData?.limit || BATCH_DETAILS_LIMIT}
                page={processingPage}
                onChange={handleChangeProcessingPage}
              />
            </div>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button className="cancel_button" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
